import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { link } from '@aline/routes';
import { alpha, Box, IconButton, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { formatRelative } from 'date-fns';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AlineButton,
  AlineCheckbox,
  AlineTooltip,
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components';
import { EditIcon } from 'src/components/icons/EditIcon';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { PlayWorkflowIcon } from 'src/components/icons/PlayWorkflowIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import type { Apollo } from 'src/gql/generated';
import {
  useAiReminderAutomations,
  useDeleteAiReminderAutomation,
  useRunAiReminderAutomation,
} from 'src/gql/hooks/aiReminderAutomation';
import { useDispatchNotification, useDispatchSuccessSnack } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { AIReminderAutomationForm } from './AIReminderAutomationForm';

const useStyles = makeStyles()(theme => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5),
  },
  cardActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  cardHighlighted: {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.main,
  },
  cardList: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'auto',
  },
  cardRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  checkbox: {
    padding: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
  },
  deleteDialog: {
    width: 500,
  },
  deleteRemindersCheckboxContainer: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  emptyContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  fieldLabel: {
    fontWeight: 600,
  },
  newAutomationButton: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  pendingBanner: {
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  pendingLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  pendingText: {
    color: 'inherit',
    fontWeight: 600,
  },
  remindersBadge: {
    alignItems: 'center',
    borderRadius: 4,
    display: 'flex',
    fontSize: 12,
    fontWeight: 700,
    height: 22,
    justifyContent: 'center',
    minWidth: 22,
    padding: theme.spacing(0, 0.5),
  },
  remindersBadgeActive: {
    backgroundColor: alpha(theme.appColors.primary, 0.7),
    color: theme.palette.common.white,
  },
  remindersBadgeEmpty: {
    backgroundColor: alpha(theme.palette.warning.main, 0.15),
    color: theme.palette.warning.dark,
  },
  skeletonCard: {
    borderRadius: 16,
    height: 88,
  },
  sparkleIcon: {
    flexShrink: 0,
    height: 14,
    width: 14,
  },
  titleText: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export type AIReminderView = 'edit' | 'list' | 'new';

interface AIReminderAutomationListProps {
  reportId: UUID;
  setView: (view: AIReminderView) => void;
  view: AIReminderView;
  highlightAutomationId?: string | null;
}

export function AIReminderAutomationList({
  highlightAutomationId,
  reportId,
  setView,
  view,
}: AIReminderAutomationListProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { runAutomation } = useRunAiReminderAutomation();
  const { deleteAutomation } = useDeleteAiReminderAutomation();
  const {
    automations,
    loading: loadingAutomations,
    refetch,
  } = useAiReminderAutomations(reportId, {
    fetchPolicy: 'cache-and-network',
  });
  const dispatchRunStarted = useDispatchSuccessSnack({
    message: 'Automation started. Reminders will appear as documents are processed.',
  });
  const dispatchWarning = useDispatchNotification({
    message: 'Automation is already running. Please wait for it to complete.',
    variant: 'warning',
  });
  const dispatchRunError = useDispatchNotification({
    message: 'Failed to run automation. Please try again.',
    variant: 'error',
  });
  const dispatchDeleteError = useDispatchNotification({
    message: 'Failed to delete automation',
    variant: 'error',
  });

  const [deleteReminders, setDeleteReminders] = useState(false);
  const [selectedAutomation, setSelectedAutomation] =
    useState<Apollo.AiReminderAutomationWithRelationsFragment | null>(null);
  const [automationToDelete, setAutomationToDelete] =
    useState<Apollo.AiReminderAutomationWithRelationsFragment | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const totalPending = useMemo(
    () => automations.reduce((sum, a) => sum + a.pendingRemindersCount, 0),
    [automations]
  );

  const handleOpenForm = useCallback(
    (automation?: Apollo.AiReminderAutomationWithRelationsFragment) => {
      setSelectedAutomation(automation ?? null);
      setView(automation ? 'edit' : 'new');
    },
    [setView]
  );

  const handleCloseForm = useCallback(() => {
    setView('list');
    setSelectedAutomation(null);
    // Refetch to get latest running state.
    refetch();
  }, [refetch, setView]);

  const handleRunAutomation = useCallback(
    async (automation: Apollo.AiReminderAutomationWithRelationsFragment) => {
      if (automation.isRunning) return dispatchWarning();

      try {
        const { error } = await runAutomation({
          variables: { automationId: automation.id },
        });
        if (!error) dispatchRunStarted();
      } catch {
        dispatchRunError();
      }
    },
    [runAutomation, dispatchRunStarted, dispatchRunError, dispatchWarning]
  );

  const handleDeleteClick = useCallback(
    (automation: Apollo.AiReminderAutomationWithRelationsFragment) => {
      setAutomationToDelete(automation);
    },
    []
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!automationToDelete) return;

    try {
      await deleteAutomation({
        variables: { automationId: automationToDelete.id, deleteReminders },
      });
      setAutomationToDelete(null);
    } catch {
      dispatchDeleteError();
    }
  }, [automationToDelete, deleteAutomation, deleteReminders, dispatchDeleteError]);

  // Highlight and scroll to automation when deep-linked via query param.
  useEffect(() => {
    if (!highlightAutomationId || automations.length === 0) return;

    setHighlightedId(highlightAutomationId);
    const element = document.getElementById(`automation-${highlightAutomationId}`);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const timer = setTimeout(() => setHighlightedId(null), 2000);
    return () => clearTimeout(timer);
  }, [highlightAutomationId, automations.length]);

  switch (view) {
    case 'edit':
    case 'new':
      return (
        <AIReminderAutomationForm
          automation={selectedAutomation}
          reportId={reportId}
          onClose={handleCloseForm}
          onSaveSuccess={handleCloseForm}
        />
      );
    case 'list': {
      if (loadingAutomations) {
        return (
          <div className={classes.container}>
            <Skeleton className={classes.skeletonCard} variant="rounded" />
            <Skeleton className={classes.skeletonCard} variant="rounded" />
            <Skeleton className={classes.skeletonCard} variant="rounded" />
          </div>
        );
      }

      if (!automations.length) {
        return (
          <div className={classes.container}>
            <div className={classes.emptyContainer}>
              <Sparkles height={48} width={48} />
              <Typography variant="subtitle1">No AI Automations Yet</Typography>
              <Typography color="textSecondary" variant="body2">
                Use Aline AI to generate reminders from your documents and stay on top of
                expirations, renewals, and key obligations.
              </Typography>
              <AlineButton color="primary" variant="contained" onClick={() => handleOpenForm()}>
                + New Automation
              </AlineButton>
            </div>
          </div>
        );
      }

      return (
        <div className={classes.container}>
          {totalPending > 0 && (
            <div className={classes.pendingBanner}>
              <div className={classes.pendingLeft}>
                <InfoIcon fill="currentColor" height={16} width={16} />
                <Typography className={classes.pendingText} variant="body2">
                  {totalPending} {totalPending === 1 ? 'reminder' : 'reminders'} pending review
                </Typography>
              </div>
              <AlineButton
                color="primary"
                size="small"
                sx={{ '&&': { backgroundColor: 'transparent' } }}
                variant="outlined"
                onClick={() =>
                  navigate(`${link.RemindersIndex()}?pendingReview=true&reportId=${reportId}`)
                }
              >
                Review now
              </AlineButton>
            </div>
          )}

          <div className={classes.cardList}>
            {automations.map(automation => {
              const statusIcon = automation.isRunning ? (
                <PulseIcon
                  className={classes.sparkleIcon}
                  variant={PulseIconVariant.LoadingPrimary}
                />
              ) : (
                <SmallAiSparkleIcon className={classes.sparkleIcon} />
              );

              const isHighlighted = highlightedId === automation.id;
              const cardClassName = clsx(classes.card, {
                [classes.cardHighlighted]: isHighlighted,
              });

              return (
                <div
                  key={automation.id}
                  className={cardClassName}
                  id={`automation-${automation.id}`}
                >
                  <div className={classes.cardRow}>
                    {statusIcon}
                    <Typography className={classes.titleText} variant="body2">
                      {automation.title || 'AI Reminder'}
                    </Typography>
                    <Box sx={{ ml: 'auto' }} />
                    <div className={classes.cardActions}>
                      <AlineTooltip
                        arrow
                        placement="top"
                        title={automation.isRunning ? 'Cannot delete while running' : 'Delete'}
                      >
                        <span style={{ display: 'flex' }}>
                          <IconButton
                            disabled={automation.isRunning}
                            size="small"
                            onClick={() => handleDeleteClick(automation)}
                          >
                            <TrashcanIcon />
                          </IconButton>
                        </span>
                      </AlineTooltip>
                      <AlineTooltip
                        arrow
                        placement="top"
                        title={automation.isRunning ? 'Cannot edit while running' : 'Edit'}
                      >
                        <span style={{ display: 'flex' }}>
                          <IconButton
                            disabled={automation.isRunning}
                            size="small"
                            onClick={() => handleOpenForm(automation)}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </AlineTooltip>
                      <AlineTooltip
                        arrow
                        placement="top"
                        title={automation.isRunning ? 'Running...' : 'Run now'}
                      >
                        <span style={{ display: 'flex' }}>
                          <IconButton
                            disabled={automation.isRunning}
                            size="small"
                            onClick={() => handleRunAutomation(automation)}
                          >
                            <PlayWorkflowIcon stroke="currentColor" />
                          </IconButton>
                        </span>
                      </AlineTooltip>
                    </div>
                  </div>
                  <div className={classes.cardRow}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography color="textSecondary" variant="caption">
                        {automation.lastTriggeredAt
                          ? `Last Run: ${formatRelative(new Date(automation.lastTriggeredAt), new Date())}`
                          : 'Last Run: not yet run'}
                      </Typography>
                      <Typography color="textSecondary" variant="caption">
                        {automation.nextRunAt
                          ? `Next Run: ${formatRelative(new Date(automation.nextRunAt), new Date())}`
                          : 'Next Run: not yet scheduled'}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }} />
                    {automation.activeRemindersCount > 0 ? (
                      <AlineTooltip
                        arrow
                        placement="top"
                        title={`${automation.activeRemindersCount} ${automation.activeRemindersCount === 1 ? 'reminder' : 'reminders'} generated`}
                      >
                        <Box sx={{ m: 0.5 }}>
                          <span
                            className={clsx(classes.remindersBadge, classes.remindersBadgeActive)}
                          >
                            {automation.activeRemindersCount}
                          </span>
                        </Box>
                      </AlineTooltip>
                    ) : (
                      automation.lastTriggeredAt && (
                        <AlineTooltip
                          arrow
                          placement="top"
                          title="No reminders have been generated"
                        >
                          <Box sx={{ m: 0.5 }}>
                            <span
                              className={clsx(classes.remindersBadge, classes.remindersBadgeEmpty)}
                            >
                              0
                            </span>
                          </Box>
                        </AlineTooltip>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <AlineButton
            className={classes.newAutomationButton}
            color="primary"
            variant="outlined"
            onClick={() => handleOpenForm()}
          >
            + New Automation
          </AlineButton>

          <ConfirmationDialog
            TransitionProps={{ onExit: () => setDeleteReminders(false) }}
            message={`Are you sure you want to delete "${automationToDelete?.title || 'AI Reminder'}"?`}
            open={Boolean(automationToDelete)}
            primaryText="Delete"
            secondaryText="Cancel"
            title="Delete Automation"
            variant={ConfirmationDialogVariant.Warn}
            onClickPrimary={handleConfirmDelete}
            onClose={() => setAutomationToDelete(null)}
          >
            <div
              className={classes.deleteRemindersCheckboxContainer}
              onClick={() => setDeleteReminders(!deleteReminders)}
            >
              <AlineCheckbox checked={deleteReminders} className={classes.checkbox} />
              <Typography variant="body2">Delete reminders generated by this automation</Typography>
            </div>
          </ConfirmationDialog>
        </div>
      );
    }
  }
}
